import React, { useState, useEffect, useCallback } from 'react';
import { supabase, TruckVisit, TruckStatus, Plant, LogisticCompany, Truck, STATUS_LABELS, STATUS_COLORS, STATUS_ORDER, SHIFT_OPTIONS } from './lib/supabase';
import { LogIn, LogOut, Plus, X, Clock, Truck as TruckIcon, Filter, ChevronDown, Download, Calendar, Settings, Building, Building2, Users, ArrowLeft, Save, Trash2, Edit as EditIcon, Activity, AlertCircle } from 'lucide-react';

// Profile type with updated roles
type UserRole = 'viewer' | 'editor' | 'admin';

interface Profile {
  id: string;
  full_name: string | null;
  phone: string | null;
  role: UserRole;
  plant_id: string | null;
  created_at: string;
}

const ROLE_HIERARCHY: Record<UserRole, number> = { viewer: 0, editor: 1, admin: 2 };

const canEdit = (role: UserRole) => ROLE_HIERARCHY[role] >= ROLE_HIERARCHY.editor;
const canAdmin = (role: UserRole) => ROLE_HIERARCHY[role] >= ROLE_HIERARCHY.admin;
const canView = (role: UserRole) => ROLE_HIERARCHY[role] >= ROLE_HIERARCHY.viewer;

// ------------------ Auth ------------------
const ALLOWED_EMAIL_DOMAIN = import.meta.env.VITE_ALLOWED_EMAIL_DOMAIN || 'yourcompany.com';

function AuthPage({ onDemo, onRegister }: { onDemo: () => void; onRegister: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'login' | 'magic'>('login');

  const handleLogin = async () => {
    if (!email.toLowerCase().endsWith(`@${ALLOWED_EMAIL_DOMAIN.toLowerCase()}`)) {
      setError(`Only @${ALLOWED_EMAIL_DOMAIN} email addresses are allowed`);
      return;
    }
    setError('');
    setLoading(true);
    let error: any = null;
    if (mode === 'login') {
      const res = await supabase.auth.signInWithPassword({ email, password });
      error = res.error;
    } else {
      const res = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin },
      });
      error = res.error;
    }
    setLoading(false);
    if (error) {
      setError(error.message);
    } else if (mode === 'magic') {
      // Magic link sent
      return;
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <TruckIcon size={48} className="auth-icon" />
        <h1>Truck Tracker</h1>
        <p className="auth-subtitle">Sign in to access the dashboard</p>
        <p className="auth-domain-hint">Only @{ALLOWED_EMAIL_DOMAIN} emails allowed</p>

        {mode === 'login' && (
          <>
            <input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              autoFocus
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
            />
            <button className="btn btn-primary" onClick={handleLogin} disabled={loading || !email || !password} style={{ width: '100%' }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div className="demo-divider">or</div>
            <button className="btn btn-ghost" style={{ width: '100%' }} onClick={() => setMode('magic')}>
              Use Magic Link Instead
            </button>
          </>
        )}

        {mode === 'magic' && (
          <>
            <input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              autoFocus
            />
            <button className="btn btn-primary" onClick={handleLogin} disabled={loading || !email} style={{ width: '100%' }}>
              {loading ? 'Sending...' : 'Send Magic Link'}
            </button>
            <div className="demo-divider">or</div>
            <button className="btn btn-ghost" style={{ width: '100%' }} onClick={() => setMode('login')}>
              Use Password Instead
            </button>
          </>
        )}

        {error && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', color: 'var(--danger)', padding: '0.75rem', borderRadius: '6px', margin: '1rem 0', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        <div className="demo-divider">or</div>
        <button className="btn btn-secondary" onClick={onDemo} style={{ width: '100%' }}>
          Try Demo (Read-Only)
        </button>
        <div className="demo-divider">or</div>
        <button className="btn btn-ghost" onClick={onRegister} style={{ width: '100%' }}>
          Create Account
        </button>
      </div>
    </div>
  );
}

// ------------------ Register Page ------------------
function RegisterPage({ onBack, onLogin }: { onBack: () => void; onLogin: () => void }) {
  const [form, setForm] = useState({ full_name: '', email: '', password: '', confirm_password: '', phone: '', website: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRegister = async () => {
    setError('');
    // Honeypot check
    if (form.website) {
      console.warn('Bot detected via honeypot');
      return;
    }
    if (!form.full_name.trim()) return setError('Full name required');
    if (!form.email.trim()) return setError('Email required');
    if (!form.email.toLowerCase().endsWith(`@${ALLOWED_EMAIL_DOMAIN.toLowerCase()}`)) {
      return setError(`Only @${ALLOWED_EMAIL_DOMAIN} email addresses are allowed`);
    }
    if (!form.password) return setError('Password required');
    if (form.password.length < 8) return setError('Password must be at least 8 characters');
    if (form.password !== form.confirm_password) return setError('Passwords do not match');

    setLoading(true);
    try {
      // Create user with email/password
      const { data, error } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: { full_name: form.full_name, phone: form.phone },
        },
      });
      if (error) throw error;

      // Explicitly create profile (doesn't rely on trigger)
      if (data.user) {
        // Use upsert to handle case where trigger already created it
        const { error: profileError } = await supabase.from('profiles').upsert({
          id: data.user.id,
          full_name: form.full_name,
          role: 'viewer',
          phone: form.phone,
        });
        if (profileError) console.warn('Profile upsert failed:', profileError);
      }

      setError('Account created! Please sign in.');
      // Auto-switch to login after short delay
      setTimeout(() => onLogin(), 2000);
    } catch (e: any) {
      setError(e.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: '420px' }}>
        <button className="icon-btn auth-back" onClick={onBack}><ArrowLeft size={20} /></button>
        <TruckIcon size={48} className="auth-icon" />
        <h1>Create Account</h1>
        <p className="auth-subtitle">Register with your company email</p>
        <p className="auth-domain-hint">Only @{ALLOWED_EMAIL_DOMAIN} emails allowed</p>

        <div className="form-grid" style={{ marginTop: '1.5rem' }}>
          <div className="field">
            <label>Full Name</label>
            <input type="text" className="input" placeholder="Your full name"
              value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} autoFocus />
          </div>
          <div className="field">
            <label>Email</label>
            <input type="email" className="input" placeholder="you@company.com"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="field">
            <label>Password</label>
            <input type="password" className="input" placeholder="Min 8 characters"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <div className="field">
            <label>Confirm Password</label>
            <input type="password" className="input" placeholder="Confirm password"
              value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} />
          </div>
          <div className="field full-width">
            <label>Phone (optional)</label>
            <input type="tel" className="input" placeholder="+91 XXXXX XXXXX"
              value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          {/* Honeypot */}
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            style={{ display: 'none' }}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
        </div>

        <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info-border)', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', fontSize: '0.9rem' }}>
          <strong>After registration:</strong> You'll get <strong>Viewer</strong> access (view all plants, export CSV).
          Admin can upgrade you to Editor (manage visits) or Admin (full access).
        </div>

        {error && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', color: 'var(--danger)', padding: '0.75rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleRegister} disabled={loading || !form.full_name || !form.email || !form.password}>
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>

        <div className="demo-divider">Already have an account?</div>
        <button className="btn btn-ghost" style={{ width: '100%' }} onClick={onLogin}>
          Sign In Instead
        </button>
      </div>
    </div>
  );
}

// ------------------ Confirm Page (Invite Flow) ------------------
function ConfirmPage({ token, onDone }: { token: string; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleConfirm = async () => {
    if (!password) return setError('Password required');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (password !== confirmPassword) return setError('Passwords do not match');

    setError('');
    setLoading(true);
    try {
      // Verify OTP token and set password
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'invite',
      });

      if (error) throw error;

      // Update user with password
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;

      // Profile should be created by ensure_profile trigger with invite metadata
      setSuccess(true);
      setTimeout(() => onDone(), 2000);
    } catch (e: any) {
      setError(e.message || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: '420px' }}>
        <TruckIcon size={48} className="auth-icon" />
        <h1>{success ? 'Account Ready' : 'Set Your Password'}</h1>
        <p className="auth-subtitle">{success ? 'Your account has been created. You can now sign in.' : 'Welcome! Create a password to complete your account setup.'}</p>

        {success ? (
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={onDone}>
            Continue to Sign In
          </button>
        ) : (
          <>
            {error && (
              <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', color: 'var(--danger)', padding: '0.75rem', borderRadius: '6px', margin: '1rem 0', fontSize: '0.9rem' }}>
                {error}
              </div>
            )}

            <div className="form-grid" style={{ marginTop: '1.5rem' }}>
              <div className="field full-width">
                <label>Password</label>
                <input type="password" className="input" placeholder="Min 8 characters"
                  value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
              </div>
              <div className="field full-width">
                <label>Confirm Password</label>
                <input type="password" className="input" placeholder="Confirm password"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>
            </div>

            <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleConfirm} disabled={loading}>
              {loading ? 'Setting up...' : 'Create Account'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ------------------ Entry Form ------------------
function EntryForm({
  plants,
  logisticCompanies,
  trucks,
  onClose,
  onSaved,
  editVisit,
  userRole,
  userPlantId,
  userId,
}: {
  plants: Plant[];
  logisticCompanies: LogisticCompany[];
  trucks: Truck[];
  onClose: () => void;
  onSaved: () => void;
  editVisit?: TruckVisit | null;
  userRole: UserRole | null;
  userPlantId: string | null;
  userId: string | null;
}) {
  const isEditor = userRole === 'editor';
  const isAdmin = userRole === 'admin';

  const [form, setForm] = useState({
    visit_date: editVisit?.visit_date?.split('T')[0] || new Date().toISOString().split('T')[0],
    registered_at: editVisit?.registered_at ? editVisit.registered_at.slice(0, 16) : new Date().toISOString().slice(0, 16),
    plant_id: editVisit?.plant_id || (isEditor && userPlantId ? userPlantId : ''),
    truck_no: editVisit?.truck_no || '',
    contact_no: editVisit?.contact_no || '',
    destination: editVisit?.destination || '',
    logistic_company_id: editVisit?.logistic_company_id || '',
    status: (editVisit?.status || 'scheduled') as TruckStatus,
    time_in: editVisit?.time_in?.split('T')[1]?.slice(0, 5) || '',
    time_out: editVisit?.time_out?.split('T')[1]?.slice(0, 5) || '',
    issue_note: editVisit?.issue_note || '',
    driver_name: editVisit?.driver_name || '',
    forklift_operator_name: editVisit?.forklift_operator_name || '',
    shift_loaded: editVisit?.shift_loaded || '',
    // Shift loading fields
    load_sequence: editVisit?.load_sequence || '',
    loaded_by: editVisit?.loaded_by || '',
    loaded_at: editVisit?.loaded_at ? editVisit.loaded_at.slice(0, 16) : '',
    load_shift: editVisit?.load_shift || '',
  });
  const [saving, setSaving] = useState(false);
  const [newCompany, setNewCompany] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const hasFormChanges = form.truck_no || form.driver_name || form.destination || form.loaded_by;

  const handleClose = () => {
    if (hasFormChanges) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  const filteredCompanies = logisticCompanies.filter(c =>
    c.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  // Viewers cannot use the form - read only
  if (!canEdit(userRole)) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editVisit ? 'View Visit' : 'New Truck Entry'}</h2>
            <button className="icon-btn" onClick={onClose}><X size={20} /></button>
          </div>
          <div className="modal-body">
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
              {userRole === 'viewer' ? 'View-only access. Contact admin for editor role.' : 'Please sign in to create or edit entries.'}
            </p>
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    if (!form.plant_id || !form.truck_no || !form.destination) {
      alert('Please fill: Plant, Truck No, and Destination');
      return;
    }

    setSaving(true);
    const payload: any = {
      visit_date: form.visit_date,
      registered_at: form.registered_at ? `${form.registered_at}:00+03:00` : new Date().toISOString(),
      plant_id: form.plant_id,
      truck_no: form.truck_no.toUpperCase(),
      contact_no: form.contact_no,
      destination: form.destination,
      logistic_company_id: form.logistic_company_id || null,
      status: form.status,
      issue_note: form.status === 'issue' ? form.issue_note : null,
      driver_name: form.driver_name,
      forklift_operator_name: form.forklift_operator_name || null,
      shift_loaded: form.shift_loaded || null,
      // Shift loading fields
      load_sequence: form.load_sequence || null,
      loaded_by: form.loaded_by || null,
      loaded_at: form.loaded_at ? `${form.loaded_at}:00+03:00` : null,
      load_shift: form.load_shift || null,
      registered_by: userId,
    };

    if (form.visit_date && form.time_in) {
      payload.time_in = `${form.visit_date}T${form.time_in}:00+03:00`;
    }
    if (form.visit_date && form.time_out) {
      payload.time_out = `${form.visit_date}T${form.time_out}:00+03:00`;
    }

    const { error } = editVisit
      ? await supabase.from('truck_visits').update(payload).eq('id', editVisit.id)
      : await supabase.from('truck_visits').insert(payload);

    setSaving(false);
    if (error) {
      alert('Error: ' + error.message);
    } else {
      onSaved();
      onClose();
    }
  };

  const addNewCompany = async () => {
    if (!newCompany.trim()) return;
    const { data, error } = await supabase.from('logistic_companies').insert({ name: newCompany.trim() }).select();
    if (error) { alert('Error: ' + error.message); return; }
    setNewCompany('');
    onSaved(); // refresh
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editVisit ? 'Edit Visit' : 'New Truck Entry'}</h2>
          <button className="icon-btn" onClick={handleClose}><X size={20} /></button>
        </div>

        <div className="form-grid">
          <div className="field">
            <label>Date</label>
            <input type="date" className="input" value={form.visit_date}
              onChange={(e) => setForm({ ...form, visit_date: e.target.value })} />
          </div>

          <div className="field">
            <label>Registered At</label>
            <input type="datetime-local" className="input" value={form.registered_at}
              onChange={(e) => setForm({ ...form, registered_at: e.target.value })} />
          </div>

          <div className="field">
            <label>Plant</label>
            {isEditor && userPlantId ? (
              <>
                <select className="input" value={userPlantId} disabled>
                  {plants.map(p => p.id === userPlantId && <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                </select>
                <p className="field-hint">Locked to your assigned plant</p>
              </>
            ) : (
              <select className="input" value={form.plant_id}
                onChange={(e) => setForm({ ...form, plant_id: e.target.value })}>
                <option value="">Select plant</option>
                {plants.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
              </select>
            )}
          </div>

          <div className="field">
            <label>Truck No</label>
            <div className="field-row">
              <input
                type="text"
                className="input"
                placeholder="Enter truck number (e.g. MH-12-AB-1234)"
                list="truck-suggestions"
                value={form.truck_no || ''}
                onChange={(e) => {
                  const truckNo = e.target.value.toUpperCase();
                  setForm({ ...form, truck_no: truckNo });
                }}
                autoComplete="off"
              />
              <datalist id="truck-suggestions">
                {/* suggestions from saved truck numbers in DB — optional */}
              </datalist>
              {form.truck_no && (
                <button className="btn btn-sm btn-ghost" onClick={() => setForm(f => ({ ...f, truck_no: '' }))}>
                  ✕ Clear
                </button>
              )}
            </div>
            <p className="field-hint">Type to search existing trucks or enter new number</p>
          </div>

          <div className="field">
            <label>Contact No</label>
            <input type="tel" className="input" placeholder="Driver phone"
              value={form.contact_no} onChange={(e) => setForm({ ...form, contact_no: e.target.value })} />
          </div>

          <div className="field">
            <label>Destination</label>
            <input type="text" className="input" placeholder="e.g. Mumbai Plant"
              value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} />
          </div>

          <div className="field">
            <label>Logistic Company</label>
            <div className="field-row">
              <input type="text" className="input" placeholder="Search or type new"
                value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} />
              {companySearch && !filteredCompanies.find(c => c.name === companySearch) && (
                <button className="btn btn-sm btn-primary" onClick={() => { setNewCompany(companySearch); addNewCompany(); }}>Add "{companySearch}"</button>
              )}
            </div>
            <select className="input" value={form.logistic_company_id}
              onChange={(e) => setForm({ ...form, logistic_company_id: e.target.value })}>
              <option value="">Select company</option>
              {filteredCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="field">
            <label>Driver Name</label>
            <input type="text" className="input" placeholder="Driver full name"
              value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} />
          </div>

          <div className="field">
            <label>Status</label>
            <select className="input" value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as TruckStatus })}>
              {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>

          {/* Shift Loading Fields - shown when status is 'loaded' */}
          {form.status === 'loaded' && (
            <>
              <div className="field">
                <label>Load Sequence</label>
                <input type="number" className="input" placeholder="1, 2, 3..." min="1"
                  value={form.load_sequence || ''}
                  onChange={(e) => setForm({ ...form, load_sequence: parseInt(e.target.value) || '' })} />
              </div>

              <div className="field">
                <label>Loaded By (Forklift Driver)</label>
                <input type="text" className="input" placeholder="Forklift operator name"
                  value={form.loaded_by} onChange={(e) => setForm({ ...form, loaded_by: e.target.value })} />
              </div>

              <div className="field">
                <label>Loaded At</label>
                <input type="datetime-local" className="input"
                  value={form.loaded_at}
                  onChange={(e) => setForm({ ...form, loaded_at: e.target.value })} />
              </div>

              <div className="field">
                <label>Load Shift</label>
                <select className="input" value={form.load_shift}
                  onChange={(e) => setForm({ ...form, load_shift: e.target.value })}>
                  <option value="">Select shift</option>
                  <option value="morning">Morning (08:30–20:30)</option>
                  <option value="night">Night (20:30–08:30)</option>
                </select>
              </div>
            </>
          )}

          {form.status === 'issue' && (
            <div className="field full-width">
              <label>Issue Note</label>
              <textarea className="input" rows={2} placeholder="Describe the issue..."
                value={form.issue_note} onChange={(e) => setForm({ ...form, issue_note: e.target.value })} />
            </div>
          )}

          <div className="field">
            <label>Time In</label>
            <input type="time" className="input" value={form.time_in}
              onChange={(e) => setForm({ ...form, time_in: e.target.value })} />
          </div>

          <div className="field">
            <label>Time Out</label>
            <input type="time" className="input" value={form.time_out}
              onChange={(e) => setForm({ ...form, time_out: e.target.value })} />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => hasFormChanges ? setShowCancelConfirm(true) : onClose()}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editVisit ? 'Update' : 'Save Entry'}
          </button>
        </div>
        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>Confirm Cancel</h2>
                <button className="icon-btn" onClick={() => setShowCancelConfirm(false)}><X size={20} /></button>
              </div>
              <div className="modal-body">
                <p>You have unsaved changes. Are you sure you want to cancel?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowCancelConfirm(false)}>Keep Editing</button>
                <button className="btn btn-primary" onClick={onClose}>Discard Changes</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------ Status Badge ------------------
function StatusBadge({ status }: { status: TruckStatus }) {
  return (
    <span className="status-badge" style={{ backgroundColor: STATUS_COLORS[status] + '20', color: STATUS_COLORS[status], borderColor: STATUS_COLORS[status] + '40' }}>
      <span className="status-dot" style={{ backgroundColor: STATUS_COLORS[status] }} />
      {STATUS_LABELS[status]}
    </span>
  );
}

// ------------------ Dashboard ------------------
function Dashboard({ onLogout, demoMode, userRole, userPlantId, onAdmin, userId }: { onLogout: () => void; demoMode: boolean; userRole: UserRole | null; userPlantId: string | null; onAdmin: () => void; userId: string | null }) {
  const [visits, setVisits] = useState<TruckVisit[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [logisticCompanies, setLogisticCompanies] = useState<LogisticCompany[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editVisit, setEditVisit] = useState<TruckVisit | null>(null);
  const [filterStatus, setFilterStatus] = useState<TruckStatus | 'all'>('all');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterTruck, setFilterTruck] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const isEditor = userRole === 'editor';
  const isAdmin = userRole === 'admin';

  const loadData = useCallback(async () => {
    let visitsQuery = supabase.from('truck_visits').select('*, plants(*), logistic_companies(*)', { count: 'exact' }).order('registered_at', { ascending: false }).limit(200);

    // Editor can only see their assigned plant's data
    if (isEditor && userPlantId) {
      visitsQuery = visitsQuery.eq('plant_id', userPlantId);
    }

    const [visitsRes, plantsRes, companiesRes, profilesRes] = await Promise.all([
      visitsQuery,
      supabase.from('plants').select('*'),
      supabase.from('logistic_companies').select('*').order('name'),
      supabase.from('profiles').select('*'),
    ]);
    if (visitsRes.data) setVisits(visitsRes.data as unknown as TruckVisit[]);
    if (plantsRes.data) setPlants(plantsRes.data);
    if (companiesRes.data) setLogisticCompanies(companiesRes.data);
    if (profilesRes.data) setProfiles(profilesRes.data);
    setLoading(false);
  }, [isEditor, userPlantId]);

  useEffect(() => { loadData(); }, [loadData]);

  const deleteVisit = async (id: string) => {
    const { error } = await supabase.from('truck_visits').delete().eq('id', id);
    if (error) {
      alert('Error: ' + error.message);
    } else {
      loadData();
    }
  };

  const filteredVisits = visits.filter(v => {
    if (filterStatus !== 'all' && v.status !== filterStatus) return false;
    if (filterDate && v.visit_date !== filterDate) return false;
    if (filterTruck && v.truck_no?.toLowerCase().includes(filterTruck.toLowerCase()) === false) return false;
    return true;
  });

  const todayVisits = filteredVisits.filter(v => v.visit_date === new Date().toISOString().split('T')[0]);
  const loadedCount = filteredVisits.filter(v => v.status === 'loaded').length;
  const issueCount = filteredVisits.filter(v => v.status === 'issue' || v.status === 'delayed').length;
  const activeCount = filteredVisits.filter(v => !['departed', 'cancelled', 'loaded'].includes(v.status)).length;

  // Closing the form from Dashboard's side. The dirty-check lives inside
  // EntryForm (which has its own hasFormChanges + cancel-confirm modal),
  // so by the time this is called the form has already handled any
  // unsaved-changes prompt. Just close cleanly.
  const closeForm = () => {
    setShowForm(false);
    setEditVisit(null);
  };

  const handleSaved = () => { loadData(); };

  const exportCSV = () => {
    const isEditor = canEdit(userRole);
    const headers = isEditor
      ? ['Date', 'Plant', 'Truck No', 'Contact', 'Driver', 'Forklift Operator', 'Shift', 'Destination', 'Company', 'Status', 'Registered At', 'Time In', 'Time Out']
      : ['Date', 'Plant', 'Truck No', 'Contact', 'Driver', 'Forklift Operator', 'Shift', 'Destination', 'Company', 'Status', 'Registered At'];
    const safeDate = (d: string | null | undefined) => {
      if (!d) return '';
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? '' : dt.toLocaleString('en-IN');
    };
    const safeTime = (d: string | null | undefined) => {
      if (!d) return '';
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? '' : dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    };
    const rows = filteredVisits.map(v => {
      const base = [
        v.visit_date,
        v.plants?.name || '',
        v.truck_no || '',
        v.contact_no,
        v.driver_name,
        v.forklift_operator_name,
        v.shift_loaded,
        v.destination,
        v.logistic_companies?.name || '',
        STATUS_LABELS[v.status],
        safeDate(v.registered_at),
      ];
      if (isEditor) {
        return [
          ...base,
          safeTime(v.time_in),
          safeTime(v.time_out),
        ];
      }
      return base;
    });
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `truck-tracker-${filterDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <TruckIcon size={24} />
          <h1>Truck Tracker</h1>
        </div>
        <div className="header-right">
          {!demoMode && userRole && canEdit(userRole) && (
            <button className="btn btn-secondary btn-sm" onClick={exportCSV} title="Download CSV">
              <Download size={16} /> Export
            </button>
          )}
          {!demoMode && userRole && canEdit(userRole) && (
            <button className="btn btn-primary" onClick={() => {
              setEditVisit(null);
              setShowForm(true);
            }}>
              <Plus size={16} /> Add Truck
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={onLogout}>
            <LogOut size={16} /> {demoMode ? 'Exit Demo' : 'Logout'}
          </button>
        </div>
      </header>
      {!demoMode && userRole === 'admin' && (
        <button className="btn btn-ghost btn-sm" onClick={onAdmin}>
          <Settings size={16} /> Admin
        </button>
      )}
      {/* DEBUG: Remove before production */}
      {!demoMode && userRole && (
        <span className="btn btn-ghost btn-sm" style={{ cursor: 'default', opacity: 0.6 }}>
          Role: {userRole}{userPlantId ? ` | Plant: ${userPlantId.slice(0,8)}...` : ' | All plants'}
        </span>
      )}
            {demoMode && (
        <div className="demo-banner">
          <span>🔒 Demo Mode — Read Only</span>
          <span className="demo-hint">Sign in with magic link to enable editing</span>
        </div>
      )}

      {/* Stats */}
      <div className="stats">
        <div className="stat-card">
          <span className="stat-value">{todayVisits.length}</span>
          <span className="stat-label">Today</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{activeCount}</span>
          <span className="stat-label">Active</span>
        </div>
        <div className="stat-card stat-loaded">
          <span className="stat-value">{loadedCount}</span>
          <span className="stat-label">Loaded</span>
        </div>
        <div className="stat-card stat-issue">
          <span className="stat-value">{issueCount}</span>
          <span className="stat-label">Issues</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-row">
          <div className="filter-group">
            <Calendar size={16} />
            <input type="date" className="input input-sm" value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)} />
          </div>
          <div className="filter-group">
            <TruckIcon size={16} />
            <input type="text" className="input input-sm" placeholder="Filter by truck no..."
              value={filterTruck}
              onChange={(e) => setFilterTruck(e.target.value)} />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} /> Status <ChevronDown size={14} />
          </button>
        </div>
        {showFilters && (
          <div className="status-filter-bar">
            <button className={`status-chip ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}>All</button>
            {STATUS_ORDER.map(s => (
              <button key={s} className={`status-chip ${filterStatus === s ? 'active' : ''}`}
                style={{ '--chip-color': STATUS_COLORS[s] } as React.CSSProperties}
                onClick={() => setFilterStatus(s)}>
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading"><div className="spinner" /> Loading...</div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Truck</th>
                <th>Plant</th>
                <th>Destination</th>
                <th>Company</th>
                <th>Status</th>
                <th>Registered At</th>
                {canEdit(userRole) && (
                  <>
                    <th>Time In</th>
                    <th>Time Out</th>
                  </>
                )}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredVisits.length === 0 && (
                <tr>
                  <td colSpan={canEdit(userRole) ? 9 : 7} className="empty-state">
                    <TruckIcon size={48} />
                    <p>No truck entries found</p>
                  </td>
                </tr>
              )}
              {filteredVisits.map(v => (
                <React.Fragment key={v.id}>
                  <tr className={`visit-row ${expanded === v.id ? 'expanded' : ''}`}
                    onClick={() => setExpanded(expanded === v.id ? null : v.id)}>
                    <td className="truck-cell">{v.truck_no || '—'}</td>
                    <td>{v.plants?.name || v.plants?.code || '—'}</td>
                    <td>{v.destination}</td>
                    <td>{v.logistic_companies?.name || '—'}</td>
                    <td><StatusBadge status={v.status} /></td>
                    <td>{v.registered_at ? new Date(v.registered_at).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    {canEdit(userRole) && (
                      <>
                        <td>{v.time_in ? new Date(v.time_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        <td>{v.time_out ? new Date(v.time_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      </>
                    )}
                    <td>
                      {canEdit(userRole) && (
                        <button className="icon-btn" onClick={(e) => { e.stopPropagation(); setEditVisit(v); setShowForm(true); }}>
                          ✏️
                        </button>
                      )}
                      {isAdmin && (
                        <button className="icon-btn" onClick={(e) => { e.stopPropagation(); if (confirm('Delete this entry?')) deleteVisit(v.id); }} style={{ color: 'var(--danger)' }}>
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === v.id && (
                    <tr className="expand-row">
                      <td colSpan={canEdit(userRole) ? 9 : 7}>
                        <div className="expand-details">
                          <div><strong>Contact:</strong> {v.contact_no || '—'}</div>
                          <div><strong>Driver:</strong> {v.driver_name || '—'}</div>
                          <div><strong>Forklift Operator:</strong> {v.forklift_operator_name || v.loaded_by || '—'}</div>
                          <div><strong>Shift:</strong> {v.shift_loaded || v.load_shift || '—'}</div>
                          <div><strong>Registered:</strong> {new Date(v.registered_at).toLocaleString('en-IN')}</div>
                          <div><strong>Registered By:</strong> {profiles.find(p => p.id === v.registered_by)?.full_name || v.registered_by || '—'}</div>
                          {v.issue_note && <div className="issue-note"><strong>Issue:</strong> {v.issue_note}</div>}
                          {canEdit(userRole) && (
                            <>
                              <div><strong>Time In:</strong> {v.time_in ? new Date(v.time_in).toLocaleString('en-IN') : '—'}</div>
                              <div><strong>Time Out:</strong> {v.time_out ? new Date(v.time_out).toLocaleString('en-IN') : '—'}</div>
                              {v.load_sequence && <div><strong>Load Sequence:</strong> {v.load_sequence}</div>}
                              {v.loaded_at && <div><strong>Loaded At:</strong> {new Date(v.loaded_at).toLocaleString('en-IN')}</div>}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  {!demoMode && canEdit(userRole) && (
                    <React.Fragment key={v.id + '-edit'}>
                      <td colSpan={canEdit(userRole) ? 9 : 7} className="edit-row">
                        <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); setEditVisit(v); setShowForm(true); }}>
                          ✏️ Edit
                        </button>
                      </td>
                    </React.Fragment>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <EntryForm
          plants={plants}
          logisticCompanies={logisticCompanies}
          trucks={trucks}
          onClose={closeForm}
          onSaved={handleSaved}
          editVisit={editVisit}
          userRole={userRole}
          userPlantId={userPlantId}
          userId={userId}
        />
      )}
    </div>
  );
}

// ------------------ Admin: Plants Management ------------------
function AdminPlants({ onBack }: { onBack: () => void }) {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editPlant, setEditPlant] = useState<Plant | null>(null);
  const [form, setForm] = useState({ code: '', name: '', address: '', timezone: 'Asia/Kolkata' });
  const [saving, setSaving] = useState(false);

  const loadPlants = useCallback(async () => {
    const { data } = await supabase.from('plants').select('*').order('code');
    if (data) setPlants(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadPlants(); }, [loadPlants]);

  const handleSave = async () => {
    if (!form.code || !form.name) { alert('Code and Name required'); return; }
    setSaving(true);
    const payload = { code: form.code.toUpperCase(), name: form.name, address: form.address, timezone: form.timezone };
    const { error } = editPlant
      ? await supabase.from('plants').update(payload).eq('id', editPlant.id)
      : await supabase.from('plants').insert(payload);
    setSaving(false);
    if (error) alert('Error: ' + error.message);
    else { loadPlants(); setShowForm(false); setEditPlant(null); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this plant?')) return;
    const { error } = await supabase.from('plants').delete().eq('id', id);
    if (error) alert('Error: ' + error.message);
    else loadPlants();
  };

  const openForm = (plant?: Plant) => {
    if (plant) {
      setEditPlant(plant);
      setForm({ code: plant.code, name: plant.name, address: plant.address || '', timezone: plant.timezone || 'Asia/Kolkata' });
    } else {
      setEditPlant(null);
      setForm({ code: '', name: '', address: '', timezone: 'Asia/Kolkata' });
    }
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditPlant(null); };

  if (loading) return <div className="loading"><div className="spinner" /> Loading...</div>;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={16} /> Back</button>
        <h1>Plants Management</h1>
      </div>
      <button className="btn btn-primary" onClick={() => openForm()}><Plus size={16} /> Add Plant</button>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr><th>Code</th><th>Name</th><th>Address</th><th>Timezone</th><th></th></tr>
          </thead>
          <tbody>
            {plants.length === 0 && <tr><td colSpan={5} className="empty-state"><Building size={48} /><p>No plants yet</p></td></tr>}
            {plants.map(p => (
              <tr key={p.id}>
                <td className="truck-cell">{p.code}</td>
                <td>{p.name}</td>
                <td>{p.address || '—'}</td>
                <td>{p.timezone}</td>
                <td>
                  <button className="icon-btn" onClick={() => openForm(p)}><EditIcon size={16} /></button>
                  <button className="icon-btn" onClick={() => handleDelete(p.id)} style={{color: 'var(--danger)'}}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>{editPlant ? 'Edit Plant' : 'Add Plant'}</h2><button className="icon-btn" onClick={closeForm}><X size={20} /></button></div>
            <div className="form-grid">
              <div className="field"><label>Code</label><input type="text" className="input" value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} disabled={!!editPlant} /></div>
              <div className="field"><label>Name</label><input type="text" className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div className="field full-width"><label>Address</label><input type="text" className="input" value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <div className="field"><label>Timezone</label><input type="text" className="input" value={form.timezone} onChange={e => setForm({...form, timezone: e.target.value})} /></div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={closeForm}>Cancel</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editPlant ? 'Update' : 'Add Plant'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------ Admin: Logistic Companies Management ------------------
function AdminCompanies({ onBack }: { onBack: () => void }) {
  const [companies, setCompanies] = useState<LogisticCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCompany, setEditCompany] = useState<LogisticCompany | null>(null);
  const [form, setForm] = useState({ name: '', contact_person: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);

  const loadCompanies = useCallback(async () => {
    const { data } = await supabase.from('logistic_companies').select('*').order('name');
    if (data) setCompanies(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const handleSave = async () => {
    if (!form.name) { alert('Name required'); return; }
    setSaving(true);
    const payload = { name: form.name, contact_person: form.contact_person, phone: form.phone, email: form.email };
    const { error } = editCompany
      ? await supabase.from('logistic_companies').update(payload).eq('id', editCompany.id)
      : await supabase.from('logistic_companies').insert(payload);
    setSaving(false);
    if (error) alert('Error: ' + error.message);
    else { loadCompanies(); setShowForm(false); setEditCompany(null); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this company?')) return;
    const { error } = await supabase.from('logistic_companies').delete().eq('id', id);
    if (error) alert('Error: ' + error.message);
    else loadCompanies();
  };

  const openForm = (company?: LogisticCompany) => {
    if (company) {
      setEditCompany(company);
      setForm({ name: company.name, contact_person: company.contact_person || '', phone: company.phone || '', email: company.email || '' });
    } else {
      setEditCompany(null);
      setForm({ name: '', contact_person: '', phone: '', email: '' });
    }
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditCompany(null); };

  if (loading) return <div className="loading"><div className="spinner" /> Loading...</div>;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={16} /> Back</button>
        <h1>Logistic Companies</h1>
      </div>
      <button className="btn btn-primary" onClick={() => openForm()}><Plus size={16} /> Add Company</button>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Contact Person</th><th>Phone</th><th>Email</th><th></th></tr>
          </thead>
          <tbody>
            {companies.length === 0 && <tr><td colSpan={5} className="empty-state"><Building2 size={48} /><p>No companies yet</p></td></tr>}
            {companies.map(c => (
              <tr key={c.id}>
                <td className="truck-cell">{c.name}</td>
                <td>{c.contact_person || '—'}</td>
                <td>{c.phone || '—'}</td>
                <td>{c.email || '—'}</td>
                <td>
                  <button className="icon-btn" onClick={() => openForm(c)}><EditIcon size={16} /></button>
                  <button className="icon-btn" onClick={() => handleDelete(c.id)} style={{color: 'var(--danger)'}}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>{editCompany ? 'Edit Company' : 'Add Company'}</h2><button className="icon-btn" onClick={closeForm}><X size={20} /></button></div>
            <div className="form-grid">
              <div className="field"><label>Name</label><input type="text" className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div className="field"><label>Contact Person</label><input type="text" className="input" value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} /></div>
              <div className="field"><label>Phone</label><input type="tel" className="input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
              <div className="field"><label>Email</label><input type="email" className="input" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={closeForm}>Cancel</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editCompany ? 'Update' : 'Add Company'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------ Admin: Users Management ------------------
function AdminUsers({ onBack }: { onBack: () => void }) {
  const [users, setUsers] = useState<any[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'users' | 'audit' | 'invite'>('users');
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'viewer' as 'viewer' | 'editor' | 'admin', plant_id: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [impersonating, setImpersonating] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    const [usersRes, plantsRes] = await Promise.all([
      supabase.from('profiles').select('*').order('created_at', { ascending: false }),
      supabase.from('plants').select('*').order('code'),
    ]);
    if (usersRes.data) setUsers(usersRes.data);
    if (plantsRes.data) setPlants(plantsRes.data);
    setLoading(false);
  }, []);

  const loadAuditLogs = useCallback(async () => {
    setAuditLoading(true);
    const { data } = await supabase.from('audit_log').select('*').order('created_at', { ascending: false }).limit(100);
    if (data) setAuditLogs(data);
    setAuditLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (activeTab === 'audit') loadAuditLogs(); }, [activeTab, loadAuditLogs]);

  const handleRoleChange = async (userId: string, newRole: 'viewer' | 'editor' | 'admin') => {
    const user = users.find(u => u.id === userId);
    const oldRole = user?.role;
    setSaving(userId);
    const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', userId);
    if (!error) {
      await supabase.rpc('log_audit_action', {
        p_action: 'role_change',
        p_target_user_id: userId,
        p_old_value: { role: oldRole },
        p_new_value: { role: newRole },
      });
    }
    setSaving(null);
    if (error) alert('Error: ' + error.message);
    else loadData();
  };

  const handlePlantChange = async (userId: string, plantId: string | null) => {
    const user = users.find(u => u.id === userId);
    const oldPlant = user?.plant_id;
    setSaving(userId);
    const { error } = await supabase.from('profiles').update({ plant_id: plantId || null }).eq('id', userId);
    if (!error) {
      await supabase.rpc('log_audit_action', {
        p_action: 'plant_change',
        p_target_user_id: userId,
        p_old_value: { plant_id: oldPlant },
        p_new_value: { plant_id: plantId },
      });
    }
    setSaving(null);
    if (error) alert('Error: ' + error.message);
    else loadData();
  };

  const handleDeleteUser = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!confirm(`Delete user "${user?.full_name || userId}"? This cannot be undone.`)) return;
    setSaving(userId);
    const { error } = await supabase.from('profiles').delete().eq('id', userId);
    if (!error) {
      await supabase.rpc('log_audit_action', {
        p_action: 'user_delete',
        p_target_user_id: userId,
        p_old_value: { full_name: user?.full_name, role: user?.role, plant_id: user?.plant_id },
      });
    }
    setSaving(null);
    if (error) alert('Error: ' + error.message);
    else loadData();
  };

  const handleBulkAction = async (action: 'role' | 'plant', value: string) => {
    if (selectedUsers.size === 0) return alert('No users selected');
    setBulkSaving(true);
    const userIds = Array.from(selectedUsers);
    const updates = userIds.map(id => supabase.from('profiles').update({ [action]: value || null }).eq('id', id));
    const results = await Promise.all(updates);
    const errors = results.filter(r => r.error);
    if (!errors.length) {
      await supabase.rpc('log_audit_action', {
        p_action: 'bulk_update',
        p_metadata: { field: action, value, count: userIds.length, user_ids: userIds },
      });
    }
    setBulkSaving(false);
    setSelectedUsers(new Set());
    if (errors.length) alert(`${errors.length} user(s) failed to update`);
    else loadData();
  };

  const handleInvite = async () => {
    if (!inviteForm.email) return alert('Email required');
    if (!inviteForm.email.toLowerCase().endsWith(`@${(import.meta.env.VITE_ALLOWED_EMAIL_DOMAIN || 'yourcompany.com').toLowerCase()}`)) {
      return alert(`Only @${import.meta.env.VITE_ALLOWED_EMAIL_DOMAIN || 'yourcompany.com'} emails allowed`);
    }
    setInviteLoading(true);
    try {
      // Use the current user's access token (not the anon key) so the
      // Edge Function can verify the caller is an admin via
      // supabase.auth.getUser(jwt). Anon-key bearer here would let anyone
      // who knows the project URL spam invites.
      const { data: { session } } = await supabase.auth.getSession();
      const bearer = session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${bearer}`,
        },
        body: JSON.stringify({
          email: inviteForm.email,
          role: inviteForm.role,
          plant_id: inviteForm.plant_id || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send invitation');

      await supabase.rpc('log_audit_action', {
        p_action: 'user_invite',
        p_new_value: { email: inviteForm.email, role: inviteForm.role, plant_id: inviteForm.plant_id },
      });
      setInviteForm({ email: '', role: 'viewer', plant_id: '' });
      setInviteLoading(false);
      alert('Invitation sent!');
      loadData();
    } catch (error) {
      setInviteLoading(false);
      alert('Error: ' + error.message);
    }
  };

  const exportUsersCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Plant', 'Created At', 'Last Sign In'];
    const rows = users.map(u => [
      u.full_name || '',
      u.id,
      u.role,
      plants.find(p => p.id === u.plant_id)?.name || 'All Plants',
      u.created_at ? new Date(u.created_at).toLocaleString('en-IN') : '',
      u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString('en-IN') : 'Never',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSelect = (userId: string) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const filtered = users.filter(u =>
      u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.role.toLowerCase().includes(search.toLowerCase()) ||
      u.id.toLowerCase().includes(search.toLowerCase())
    );
    if (selectedUsers.size === filtered.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filtered.map(u => u.id)));
    }
  };

  const filteredUsers = users.filter(u =>
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.role.toLowerCase().includes(search.toLowerCase()) ||
    u.id.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="loading"><div className="spinner" /> Loading...</div>;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={16} /> Back</button>
        <h1>User Management</h1>
      </div>

      {/* Tab Navigation */}
      <div className="tab-bar" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>
        <button className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>Users</button>
        <button className={`tab-btn ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')}>Audit Log</button>
        <button className={`tab-btn ${activeTab === 'invite' ? 'active' : ''}`} onClick={() => setActiveTab('invite')}>Invite User</button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="filter-row" style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <input type="text" className="input input-sm" placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: '200px' }} />
            {selectedUsers.size > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span>{selectedUsers.size} selected</span>
                <select className="input input-sm" onChange={e => handleBulkAction('role', e.target.value)} defaultValue="">
                  <option value="">Bulk: Set Role</option>
                  <option value="viewer">→ Viewer</option>
                  <option value="editor">→ Editor</option>
                  <option value="admin">→ Admin</option>
                </select>
                <select className="input input-sm" onChange={e => handleBulkAction('plant', e.target.value)} defaultValue="" disabled={bulkSaving}>
                  <option value="">Bulk: Set Plant</option>
                  <option value="">→ Clear (All Plants)</option>
                  {plants.map(p => <option key={p.id} value={p.id}>→ {p.code} - {p.name}</option>)}
                </select>
                <button className="btn btn-secondary btn-sm" onClick={exportUsersCSV} disabled={bulkSaving}><Download size={14} /> Export CSV</button>
              </div>
            )}
          </div>

          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input type="checkbox" checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0} onChange={toggleSelectAll} />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Plant</th>
                  <th>Created</th>
                  <th>Last Sign In</th>
                  <th>Status</th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 && <tr><td colSpan={9} className="empty-state"><Users size={48} /><p>No users found</p></td></tr>}
                {filteredUsers.map(u => (
                  <tr key={u.id}>
                    <td>
                      <input type="checkbox" checked={selectedUsers.has(u.id)} onChange={() => toggleSelect(u.id)} />
                    </td>
                    <td className="truck-cell">{u.full_name || '—'}</td>
                    <td>{u.id}</td>
                    <td>
                      <select
                        className="input input-sm"
                        value={u.role}
                        onChange={e => handleRoleChange(u.id, e.target.value as 'viewer' | 'editor' | 'admin')}
                        disabled={saving === u.id || impersonating === u.id}
                      >
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td>
                      <select
                        className="input input-sm"
                        value={u.plant_id || ''}
                        onChange={e => handlePlantChange(u.id, e.target.value)}
                        disabled={saving === u.id || u.role === 'admin' || u.role === 'viewer' || impersonating === u.id}
                      >
                        <option value="">— All Plants —</option>
                        {plants.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                      </select>
                    </td>
                    <td>{u.created_at ? new Date(u.created_at).toLocaleString('en-IN') : '—'}</td>
                    <td>{u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString('en-IN') : 'Never'}</td>
                    <td>
                      <span className={`status-dot ${u.last_sign_in_at && (Date.now() - new Date(u.last_sign_in_at).getTime() < 24*60*60*1000) ? 'online' : 'offline'}`} />
                      {u.last_sign_in_at && (Date.now() - new Date(u.last_sign_in_at).getTime() < 24*60*60*1000) ? 'Active' : 'Inactive'}
                    </td>
                    <td style={{ display: 'flex', gap: '0.25rem' }}>
                      {impersonating === u.id ? (
                        <button className="btn btn-sm btn-warning" onClick={() => setImpersonating(null)}>Stop Impersonate</button>
                      ) : u.role !== 'admin' && !impersonating && (
                        <button className="btn btn-sm btn-ghost" onClick={() => setImpersonating(u.id)} title="View as this user"><Users size={14} /></button>
                      )}
                      <button className="btn btn-sm btn-ghost" onClick={() => handleDeleteUser(u.id)} disabled={saving === u.id || u.role === 'admin'} style={{ color: 'var(--danger)' }}><Trash2 size={14} /></button>
                      {saving === u.id && <span className="spinner-sm" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {impersonating && (
            <div className="impersonate-banner" style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', padding: '1rem', borderRadius: '8px', marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>🔍 Impersonating: <strong>{users.find(u => u.id === impersonating)?.full_name || impersonating}</strong> — Dashboard will show their view</span>
              <button className="btn btn-sm btn-secondary" onClick={() => setImpersonating(null)}>Stop</button>
            </div>
          )}
        </div>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && (
        <div>
          <div className="filter-row" style={{ marginBottom: '1rem' }}>
            <button className="btn btn-secondary btn-sm" onClick={loadAuditLogs} disabled={auditLoading}><Download size={14} /> Refresh</button>
          </div>
          {auditLoading ? (
            <div className="loading"><div className="spinner" /> Loading...</div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr><th>Time</th><th>Admin</th><th>Action</th><th>Target User</th><th>Changes</th></tr>
                </thead>
                <tbody>
                  {auditLogs.length === 0 && <tr><td colSpan={5} className="empty-state"><Activity size={48} /><p>No audit logs</p></td></tr>}
                  {auditLogs.map(log => (
                    <tr key={log.id}>
                      <td>{new Date(log.created_at).toLocaleString('en-IN')}</td>
                      <td>{log.admin_name || log.admin_id}</td>
                      <td><span className="status-badge" style={{ backgroundColor: 'var(--primary-bg)', color: 'var(--primary)', borderColor: 'var(--primary-border)' }}>{log.action}</span></td>
                      <td>{log.target_user_name || log.target_user_id || '—'}</td>
                      <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {log.old_value && log.new_value ? `Old: ${JSON.stringify(log.old_value)} → New: ${JSON.stringify(log.new_value)}` : log.metadata ? JSON.stringify(log.metadata) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Invite User Tab */}
      {activeTab === 'invite' && (
        <div className="invite-form" style={{ maxWidth: '500px' }}>
          <h3>Invite New User</h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>Sends a magic link invitation. User will be pre-assigned role and plant on first login.</p>
          <div className="form-grid">
            <div className="field">
              <label>Email</label>
              <input type="email" className="input" placeholder="user@company.com" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} />
            </div>
            <div className="field">
              <label>Role</label>
              <select className="input" value={inviteForm.role} onChange={e => setInviteForm({...inviteForm, role: e.target.value as 'viewer' | 'editor' | 'admin'})}>
                <option value="viewer">Viewer (Logistics - all plants)</option>
                <option value="editor">Editor (Warehouse - one plant)</option>
                <option value="admin">Admin (Full access)</option>
              </select>
            </div>
            <div className="field">
              <label>Plant (for Editors)</label>
              <select className="input" value={inviteForm.plant_id} onChange={e => setInviteForm({...inviteForm, plant_id: e.target.value})} disabled={inviteForm.role !== 'editor'}>
                <option value="">Select plant</option>
                {plants.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
              </select>
            </div>
          </div>
          <div className="modal-footer" style={{ marginTop: '1rem' }}>
            <button className="btn btn-primary" onClick={handleInvite} disabled={inviteLoading}>{inviteLoading ? 'Sending...' : 'Send Invitation'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------ Admin Dashboard ------------------
function AdminDashboard({ onBack }: { onBack: () => void }) {
  return (
    <div className="admin-page">
      <div className="admin-header">
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={16} /> Back to Dashboard</button>
        <h1>Administration</h1>
      </div>
      <div className="admin-cards">
        <button className="admin-card" onClick={() => window.dispatchEvent(new CustomEvent('admin-nav', { detail: 'plants' }))}>
          <Building size={32} /><h3>Plants</h3><p>Manage warehouse locations</p>
        </button>
        <button className="admin-card" onClick={() => window.dispatchEvent(new CustomEvent('admin-nav', { detail: 'companies' }))}>
          <Building2 size={32} /><h3>Logistic Companies</h3><p>Manage carrier companies</p>
        </button>
        <button className="admin-card" onClick={() => window.dispatchEvent(new CustomEvent('admin-nav', { detail: 'users' }))}>
          <Users size={32} /><h3>Users</h3><p>Manage roles & plant access</p>
        </button>
      </div>
    </div>
  );
}

// ------------------ App ------------------
export default function App() {
  const [session, setSession] = useState<any>(null);
  const [demoMode, setDemoMode] = useState(false);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [adminView, setAdminView] = useState<'dashboard' | 'plants' | 'companies' | 'users' | null>(null);
  const [showRegister, setShowRegister] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      if (session?.user?.id) {
        // Try to get existing profile
        const { data: profile, error } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();

        if (error && error.code === 'PGRST116') {
          // Profile not found - create it via ensure_profile function
          console.log('Profile not found, creating via ensure_profile...');
          const { error: rpcError } = await supabase.rpc('ensure_profile', { user_id: session.user.id });
          if (rpcError) {
            console.error('ensure_profile failed:', rpcError);
            // Fallback: manual insert
            await supabase.from('profiles').insert({
              id: session.user.id,
              full_name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0],
              role: 'viewer',
              phone: session.user.user_metadata?.phone,
            });
          }
          // Re-fetch
          const { data: newProfile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          setUserProfile(newProfile);
        } else {
          setUserProfile(profile);
        }
      } else {
        setUserProfile(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handler = (e: CustomEvent) => setAdminView(e.detail);
    window.addEventListener('admin-nav', handler as EventListener);
    return () => window.removeEventListener('admin-nav', handler as EventListener);
  }, []);

  const isAdmin = userProfile?.role === 'admin';

  // Handle confirm page (invite flow - set password)
  const [confirmToken, setConfirmToken] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token') || params.get('invite_token');
    if (token) setConfirmToken(token);
  }, []);

  if (confirmToken) {
    return <ConfirmPage token={confirmToken} onDone={() => setConfirmToken(null)} />;
  }

  if (!session && !demoMode) {
    if (showRegister) {
      return <RegisterPage onBack={() => setShowRegister(false)} onLogin={() => setShowRegister(false)} />;
    }
    return <AuthPage onDemo={() => setDemoMode(true)} onRegister={() => setShowRegister(true)} />;
  }

  // Admin views
  if (adminView === 'dashboard') return <AdminDashboard onBack={() => setAdminView(null)} />;
  if (adminView === 'plants') return <AdminPlants onBack={() => setAdminView('dashboard')} />;
  if (adminView === 'companies') return <AdminCompanies onBack={() => setAdminView('dashboard')} />;
  if (adminView === 'users') return <AdminUsers onBack={() => setAdminView('dashboard')} />;

  return <Dashboard onLogout={() => { supabase.auth.signOut(); setDemoMode(false); }} demoMode={demoMode} userRole={userProfile?.role} userPlantId={userProfile?.plant_id || null} onAdmin={() => setAdminView('dashboard')} userId={session?.user?.id || null} />;
}